import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function Stats() {
  const { t } = useI18n();
  return (
    <section className="relative bg-white border-t border-slate-100">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {t.stats.items.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="border-t border-slate-200 pt-6"
            >
              <div className="font-display font-medium text-slate-900 text-4xl md:text-5xl lg:text-6xl leading-none tracking-tight">
                {s.k.replace("+", "")}<span className="text-[var(--steel)]">+</span>
              </div>
              <div className="label-eyebrow mt-4">{s.v}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTASection() {
  const { t } = useI18n();
  return (
    <section className="relative bg-white border-t border-slate-100">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="relative bg-[var(--bg-dark)] rounded-2xl overflow-hidden shadow-xl">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0 bg-gradient-to-tr from-[var(--steel)] via-transparent to-transparent" />
          </div>
          <div className="relative p-8 md:p-16 lg:p-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="font-display font-medium text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl"
              >
                {t.cta.title}
              </motion.h2>
              <p className="mt-5 text-white/70 max-w-xl">{t.cta.sub}</p>
            </div>
            <div className="md:col-span-4 flex flex-wrap md:justify-end items-center gap-3">
              <Link to="/contact" className="btn-white-on-dark inline-flex items-center gap-2 px-6 py-4 text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full">
                {t.cta.btn} <ArrowUpRight size={14} />
              </Link>
              <a href="https://wa.me/919731852956" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-4 border border-white/25 text-white text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full hover:bg-white/10 transition-colors">
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FAQSection() {
  const { t } = useI18n();
  return (
    <section className="relative bg-white border-t border-slate-100">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
        <div className="md:col-span-4">
          <div className="label-eyebrow mb-4">{t.faq.eyebrow}</div>
          <h2 className="font-display font-medium text-slate-900 text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">{t.faq.title}</h2>
        </div>
        <div className="md:col-span-8">
          <Accordion type="single" collapsible className="w-full">
            {t.faq.items.map((f, i) => (
              <AccordionItem key={i} value={`i-${i}`} className="border-slate-200">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-slate-900 text-lg md:text-xl py-5 hover:no-underline hover:text-[var(--steel)]">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-[15px] leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

/* ---------- Ad-friendly reserved slot ---------- */
export function AdSlot({ id = "adslot-home", label = "Sponsored space · AdSense-ready" }) {
  return (
    <section className="bg-white">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-8">
        <div id={id} className="ad-slot" data-testid={id}>
          {label}
        </div>
      </div>
    </section>
  );
}
